/**
 * Tool options: colour swatches, three widths, size slider (text, symbol, connector pin), symbol
 * button and tool hints — a row, or a column for the toolbar docked to the left / right edge (then
 * the size is a − / + stepper and the hint is left out).
 */
import { Minus, Music, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { CONNECTOR_SIZE_RANGE, HIGHLIGHT_COLORS, HIGHLIGHT_WIDTHS, INK_COLORS, PEN_COLORS, PEN_WIDTHS, STAMP_SIZE_RANGE } from '../../shared/annotations';
import { SMUFL_BY_NAME } from '../../shared/smufl';
import { cx } from '../components/ui';
import { useT, type MessageKey } from '../i18n';
import { PinMark } from '../viewer/Pins';
import { GlyphIcon } from './GlyphIcon';
import { TEXT_SIZE_RANGE, type ToolPrefs } from './tools';

const COLOR_NAMES: Record<string, MessageKey> = {
  '#111111': 'annotate.color_black',
  '#1f4fd1': 'annotate.color_blue',
  '#d32f2f': 'annotate.color_red',
  '#2e7d32': 'annotate.color_green',
  '#ef8a17': 'annotate.color_orange',
  '#ffe14d': 'annotate.color_yellow',
  '#7ee081': 'annotate.color_lightgreen',
  '#ff8fc8': 'annotate.color_pink',
  '#7cc7ff': 'annotate.color_lightblue',
  '#ffffff': 'annotate.color_white',
};

export function ColorSwatches({
  colors,
  value,
  onChange,
  vertical,
}: {
  colors: readonly string[];
  value: string;
  onChange: (c: string) => void;
  vertical?: boolean;
}) {
  const t = useT();
  return (
    <div role="radiogroup" aria-label={t('annotate.colors')} className={cx('flex shrink-0 items-center', vertical && 'flex-col')}>
      {colors.map((c) => {
        const on = c.toLowerCase() === value.toLowerCase();
        const name = COLOR_NAMES[c.toLowerCase()];
        return (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={name ? t(name) : c}
            title={name ? t(name) : c}
            onClick={() => onChange(c)}
            className="flex size-11 items-center justify-center rounded-full"
          >
            <span
              className={cx('size-8 rounded-full ring-offset-2 ring-offset-[var(--surface)] transition', on ? 'ring-[3px] ring-[var(--accent)]' : 'ring-0')}
              style={{ background: c, boxShadow: 'inset 0 0 0 1px rgb(0 0 0 / 0.18)' }}
            />
          </button>
        );
      })}
    </div>
  );
}

const WIDTH_LABELS: MessageKey[] = ['annotate.width_thin', 'annotate.width_medium', 'annotate.width_thick'];

export function WidthPicker({
  value,
  onChange,
  color,
  px,
  highlight,
  vertical,
}: {
  value: number;
  onChange: (i: number) => void;
  color: string;
  /** Line thickness of each preset in the icon (CSS px). */
  px: readonly number[];
  highlight?: boolean;
  vertical?: boolean;
}) {
  const t = useT();
  return (
    <div role="radiogroup" aria-label={t('annotate.width')} className={cx('flex shrink-0 items-center gap-0.5', vertical && 'flex-col')}>
      {px.map((w, i) => {
        const on = i === value;
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={t(WIDTH_LABELS[i] ?? 'annotate.width')}
            title={t(WIDTH_LABELS[i] ?? 'annotate.width')}
            onClick={() => onChange(i)}
            className={cx('flex size-11 items-center justify-center rounded-xl transition', on ? 'bg-[var(--accent-soft)] ring-2 ring-[var(--accent)]' : 'hover:bg-[var(--surface-2)]')}
          >
            <span
              className="block w-6 rounded-full"
              style={{
                height: w,
                background: color,
                opacity: highlight ? 0.7 : 1,
                boxShadow: highlight || color.toLowerCase() === '#ffffff' ? 'inset 0 0 0 1px rgb(0 0 0 / 0.25)' : undefined,
              }}
            />
          </button>
        );
      })}
    </div>
  );
}

export function SizeSlider({
  value,
  min,
  max,
  onChange,
  preview,
  vertical,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  preview?: ReactNode;
  /** A − / + stepper (tenths of the range) instead of the slider. */
  vertical?: boolean;
}) {
  const t = useT();
  if (vertical) {
    const step = Math.max(10, Math.round((max - min) / 10 / 10) * 10);
    const stepBtn = 'flex size-11 items-center justify-center rounded-xl text-[var(--ink-2)] hover:bg-[var(--surface-2)] disabled:opacity-35';
    return (
      <div role="group" aria-label={t('annotate.size')} className="flex shrink-0 flex-col items-center">
        <button type="button" className={stepBtn} aria-label={t('annotate.sel_larger')} title={t('annotate.sel_larger')} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))}>
          <Plus className="size-5" aria-hidden />
        </button>
        {preview ?? (
          <span className="text-[13px] font-semibold text-[var(--muted)] tabular-nums" aria-hidden>
            {Math.round(((value - min) / (max - min)) * 100)}%
          </span>
        )}
        <button type="button" className={stepBtn} aria-label={t('annotate.sel_smaller')} title={t('annotate.sel_smaller')} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))}>
          <Minus className="size-5" aria-hidden />
        </button>
      </div>
    );
  }
  return (
    <label className="flex min-h-11 shrink-0 items-center gap-2 px-1">
      <span className="text-[14px] font-semibold text-[var(--muted)]">{t('annotate.size')}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={10}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={t('annotate.size')}
        className="h-11 w-32 accent-[var(--accent)] sm:w-40"
      />
      {preview}
    </label>
  );
}

const Divider = ({ vertical }: { vertical: boolean }) => (
  <span className={cx('shrink-0 bg-[var(--line)]', vertical ? 'my-1 h-px w-8' : 'mx-1 h-8 w-px')} aria-hidden />
);

/**
 * At most two lines, so the options row never grows; the important part of a hint comes first.
 * `divided`: a divider before it. Left out of a column (with its divider).
 */
function Hint({ vertical, divided, children }: { vertical: boolean; divided?: boolean; children: ReactNode }) {
  if (vertical) return null;
  return (
    <>
      {divided ? <Divider vertical={false} /> : null}
      <span className="line-clamp-2 max-w-72 min-w-32 px-2 text-[15px] leading-snug font-medium text-[var(--muted)]">{children}</span>
    </>
  );
}

/** Options of the current tool (one row, scrollable; or a column — `vertical`). */
export function ToolOptions({
  prefs,
  update,
  onOpenPalette,
  vertical = false,
}: {
  prefs: ToolPrefs;
  update: (fn: (p: ToolPrefs) => ToolPrefs) => void;
  onOpenPalette: () => void;
  vertical?: boolean;
}) {
  const t = useT();
  switch (prefs.tool) {
    case 'pen':
      return (
        <>
          <ColorSwatches vertical={vertical} colors={INK_COLORS} value={prefs.pen.color} onChange={(color) => update((p) => ({ ...p, pen: { ...p.pen, color } }))} />
          <Divider vertical={vertical} />
          <WidthPicker
            vertical={vertical}
            value={prefs.pen.width}
            onChange={(width) => update((p) => ({ ...p, pen: { ...p.pen, width } }))}
            color={prefs.pen.color}
            px={PEN_WIDTHS.map((w) => Math.max(2, w / 8))}
          />
          <Hint vertical={vertical} divided>{t('annotate.hint_pen')}</Hint>
        </>
      );
    case 'highlighter':
      return (
        <>
          <ColorSwatches
            vertical={vertical}
            colors={HIGHLIGHT_COLORS}
            value={prefs.highlighter.color}
            onChange={(color) => update((p) => ({ ...p, highlighter: { ...p.highlighter, color } }))}
          />
          <Divider vertical={vertical} />
          <WidthPicker
            vertical={vertical}
            value={prefs.highlighter.width}
            onChange={(width) => update((p) => ({ ...p, highlighter: { ...p.highlighter, width } }))}
            color={prefs.highlighter.color}
            px={HIGHLIGHT_WIDTHS.map((w) => w / 14)}
            highlight
          />
          <Hint vertical={vertical} divided>{t('annotate.hint_highlighter')}</Hint>
        </>
      );
    case 'text':
      return (
        <>
          <ColorSwatches vertical={vertical} colors={PEN_COLORS} value={prefs.text.color} onChange={(color) => update((p) => ({ ...p, text: { ...p.text, color } }))} />
          <Divider vertical={vertical} />
          <SizeSlider
            vertical={vertical}
            value={prefs.text.size}
            min={TEXT_SIZE_RANGE[0]}
            max={TEXT_SIZE_RANGE[1]}
            onChange={(size) => update((p) => ({ ...p, text: { ...p.text, size } }))}
            preview={
              <span aria-hidden className="w-8 text-center font-semibold" style={{ color: prefs.text.color, fontSize: 10 + (prefs.text.size / TEXT_SIZE_RANGE[1]) * 20 }}>
                A
              </span>
            }
          />
          <Hint vertical={vertical} divided>{t('annotate.hint_text')}</Hint>
        </>
      );
    case 'stamp': {
      const g = prefs.stamp.glyph ? SMUFL_BY_NAME.get(prefs.stamp.glyph) : undefined;
      return (
        <>
          <button
            type="button"
            onClick={onOpenPalette}
            aria-label={g ? t('annotate.change_symbol') : t('annotate.choose_symbol')}
            title={g ? t('annotate.change_symbol') : t('annotate.choose_symbol')}
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-xl px-2 font-semibold text-[var(--accent)] ring-1 ring-[var(--line)] hover:bg-[var(--accent-soft)]"
          >
            {g ? <GlyphIcon glyph={g} size={36} color={prefs.stamp.color} /> : <Music className="size-6" aria-hidden />}
            {vertical ? null : <span className="text-[15px]">{g ? t('annotate.change_symbol') : t('annotate.choose_symbol')}</span>}
          </button>
          <Divider vertical={vertical} />
          <ColorSwatches vertical={vertical} colors={PEN_COLORS} value={prefs.stamp.color} onChange={(color) => update((p) => ({ ...p, stamp: { ...p.stamp, color } }))} />
          <Divider vertical={vertical} />
          <SizeSlider
            vertical={vertical}
            value={prefs.stamp.size}
            min={STAMP_SIZE_RANGE[0]}
            max={STAMP_SIZE_RANGE[1]}
            onChange={(size) => update((p) => ({ ...p, stamp: { ...p.stamp, size } }))}
          />
          {g ? (
            <Hint vertical={vertical} divided>
              {t('annotate.hint_stamp')}
            </Hint>
          ) : null}
        </>
      );
    }
    case 'connector': {
      const [lo, hi] = CONNECTOR_SIZE_RANGE;
      return (
        <>
          <ColorSwatches
            vertical={vertical}
            colors={PEN_COLORS}
            value={prefs.connector.color}
            onChange={(color) => update((p) => ({ ...p, connector: { ...p.connector, color } }))}
          />
          <Divider vertical={vertical} />
          <SizeSlider
            vertical={vertical}
            value={prefs.connector.size}
            min={lo}
            max={hi}
            onChange={(size) => update((p) => ({ ...p, connector: { ...p.connector, size } }))}
            preview={
              <span aria-hidden className="flex size-10 items-center justify-center">
                <PinMark d={10 + ((prefs.connector.size - lo) / (hi - lo)) * 26} color={prefs.connector.color} />
              </span>
            }
          />
          <Hint vertical={vertical} divided>{t('annotate.hint_connector')}</Hint>
        </>
      );
    }
    case 'eraser':
      return <Hint vertical={vertical}>{t('annotate.hint_eraser')}</Hint>;
    case 'select':
      return <Hint vertical={vertical}>{t('annotate.hint_select')}</Hint>;
  }
}
